import React from 'react';
import type { ActiveModelStatus } from '../../../shared/api/grpc/revitClient';
import type { SearchMatch } from '../../../shared/lib/utils/findReplaceUtils';
import type { FindReplaceOptions } from '../../../shared/lib/utils/findReplaceUtils';
import type { ImportPreview, ImportResult } from '../../../shared/lib/utils/csvUtils';
import type {
  BulkUpdateResult,
  CellUpdateResult,
  QuickFillSyncProgress,
} from '../../../shared/services/quickFillRevitService';
import type { WorkspaceIntroCardProps } from '../../../shared/ui/common/WorkspaceIntroCard';
import FindReplaceDialog from '../../../shared/ui/dialogs/FindReplaceDialog';
import CSVImportPreviewDialog from '../../../shared/ui/dialogs/CSVImportPreviewDialog';
import DocumentWriteGuardDialog from '../../../shared/ui/dialogs/DocumentWriteGuardDialog';
import {
  QuickFillParameterChangeDialog,
  QuickFillAutoFillPreviewDialog,
  QuickFillNavigationGuardDialog,
  QuickFillSaveReviewDialog,
} from './components';
import QuickFillApplyProgressDialog from './components/QuickFillApplyProgressDialog';
import QuickFillKeyLibraryDialog, {
  type QuickFillKeyLibraryDialogProps,
} from './components/QuickFillKeyLibraryDialog';
import type {
  QuickFillAutoFillPreview,
  QuickFillAutoFillPreviewMode,
} from './components/QuickFillAutoFillPreviewDialog';
import QuickFillGuideDialog from './QuickFillGuideDialog';

export interface QuickFillDialogShellProps {
  // Model status blocking dialog
  activeModelStatus: ActiveModelStatus | null;
  isModelWriteBlocked: boolean;
  modelBlockReason: 'no-document' | 'central-inaccessible' | 'read-only' | null;
  modelStatusDialogDismissed: boolean;
  setModelStatusDialogDismissed: (value: boolean) => void;
  refreshActiveModelStatus: () => void | Promise<void>;

  // Parameter change dialog
  parameterChangeDialog: {
    open: boolean;
    propertyId: string;
    oldParameter: string;
    newParameter: string;
  };
  handleCancelParameterChange: () => void;
  handleConfirmParameterChange: () => void;

  // Auto-fill preview dialog
  effectiveAutoFillDialogOpen: boolean;
  autoFillPreviewOnly: boolean;
  effectiveAutoFillDialogMode: QuickFillAutoFillPreviewMode;
  effectiveAutoFillDialogPreview: QuickFillAutoFillPreview;
  autoFillPreviewDialogRef: React.RefObject<HTMLDivElement | null>;
  handleCancelAutoFill: () => void;
  handleConfirmAutoFillDialog: () => void;

  // Workstation-local learned key library
  keyLibrary: QuickFillKeyLibraryDialogProps;

  // Find & replace dialog
  effectiveFindReplaceOpen: boolean;
  findReplacePreviewOnly: boolean;
  findReplaceDialogRef: React.RefObject<HTMLDivElement | null>;
  clearFindReplaceFocus: () => void;
  setFindReplaceOpen: (value: boolean) => void;
  visibleProperties: Array<{ label: string }>;
  findReplaceVisibleRowIndices: number[];
  selectedRows: number[];
  handleFind: (searchTerm: string, options: FindReplaceOptions) => SearchMatch[];
  handleReplace: (
    match: SearchMatch,
    replaceValue: string,
    searchTerm: string,
    options: FindReplaceOptions
  ) => void | Promise<void>;
  handleReplaceAll: (
    searchTerm: string,
    replaceValue: string,
    options: FindReplaceOptions
  ) => number | Promise<number>;
  handleFindReplaceHighlight: (match: SearchMatch | null) => void;

  // CSV import dialog
  csvImportOpen: boolean;
  setCSVImportOpen: (value: boolean) => void;
  handleCSVPreview: (file: File) => Promise<ImportPreview>;
  handleCSVImport: (file: File) => Promise<ImportResult>;

  // Save review dialog
  effectiveSaveReviewOpen: boolean;
  saveReviewPreviewOnly: boolean;
  saveReviewDialogRef: React.RefObject<HTMLDivElement | null>;
  effectiveSaveReviewWarning: string | undefined;
  effectiveSaveReviewResult: BulkUpdateResult | null;
  successRows: CellUpdateResult[];
  failedRows: CellUpdateResult[];
  setSaveResultDialog: (value: { open: boolean; result: BulkUpdateResult | null; warning?: string }) => void;
  onContinue: () => void;

  // Navigation guard dialog
  pendingNavigation: (() => void) | null;
  isContinuing: boolean;
  setPendingNavigation: (value: (() => void) | null) => void;

  // Apply progress dialog
  showQuickFillApplyProgress: boolean;
  syncProgress: QuickFillSyncProgress | null | undefined;

  // Guide dialog
  guideOpen: boolean;
  setGuideOpen: (value: boolean) => void;
  handleOpenTour: (options?: { restart?: boolean }) => void;
  quickFillGuideContent: WorkspaceIntroCardProps;
}

const QuickFillDialogShell: React.FC<QuickFillDialogShellProps> = (props) => {
  const {
    activeModelStatus,
    isModelWriteBlocked,
    modelBlockReason,
    modelStatusDialogDismissed,
    setModelStatusDialogDismissed,
    refreshActiveModelStatus,
    parameterChangeDialog,
    handleCancelParameterChange,
    handleConfirmParameterChange,
    effectiveAutoFillDialogOpen,
    autoFillPreviewOnly,
    effectiveAutoFillDialogMode,
    effectiveAutoFillDialogPreview,
    autoFillPreviewDialogRef,
    handleCancelAutoFill,
    handleConfirmAutoFillDialog,
    keyLibrary,
    effectiveFindReplaceOpen,
    findReplacePreviewOnly,
    findReplaceDialogRef,
    clearFindReplaceFocus,
    setFindReplaceOpen,
    visibleProperties,
    findReplaceVisibleRowIndices,
    selectedRows,
    handleFind,
    handleReplace,
    handleReplaceAll,
    handleFindReplaceHighlight,
    csvImportOpen,
    setCSVImportOpen,
    handleCSVPreview,
    handleCSVImport,
    effectiveSaveReviewOpen,
    saveReviewPreviewOnly,
    saveReviewDialogRef,
    effectiveSaveReviewWarning,
    effectiveSaveReviewResult,
    successRows,
    failedRows,
    setSaveResultDialog,
    onContinue,
    pendingNavigation,
    isContinuing,
    setPendingNavigation,
    showQuickFillApplyProgress,
    syncProgress,
    guideOpen,
    setGuideOpen,
    handleOpenTour,
    quickFillGuideContent,
  } = props;

  const supportingFlags: string[] = [];
  if (activeModelStatus?.isReadOnlyFile) supportingFlags.push('the .rvt file has the OS ReadOnly attribute');
  if (activeModelStatus && !activeModelStatus.isModifiable) supportingFlags.push('Document.IsModifiable is false (transient regeneration state)');

  return (
    <>
      <DocumentWriteGuardDialog
        open={!modelStatusDialogDismissed && Boolean(activeModelStatus?.success) && isModelWriteBlocked}
        blockReason={modelBlockReason}
        activeModelStatus={activeModelStatus}
        supportingFlags={supportingFlags}
        onRecheck={refreshActiveModelStatus}
        onDismiss={() => setModelStatusDialogDismissed(true)}
      />

      <QuickFillParameterChangeDialog
        open={parameterChangeDialog.open}
        oldParameter={parameterChangeDialog.oldParameter}
        newParameter={parameterChangeDialog.newParameter}
        onClose={handleCancelParameterChange}
        onConfirm={handleConfirmParameterChange}
      />

      <QuickFillAutoFillPreviewDialog
        open={effectiveAutoFillDialogOpen}
        previewOnly={autoFillPreviewOnly}
        mode={effectiveAutoFillDialogMode}
        preview={effectiveAutoFillDialogPreview}
        contentRef={autoFillPreviewDialogRef}
        onClose={handleCancelAutoFill}
        onApply={handleConfirmAutoFillDialog}
      />

      <QuickFillKeyLibraryDialog {...keyLibrary} />

      {/* Find & Replace Dialog */}
      <FindReplaceDialog
        open={effectiveFindReplaceOpen}
        previewOnly={findReplacePreviewOnly}
        contentRef={findReplaceDialogRef}
        onClose={() => {
          if (findReplacePreviewOnly) {
            return;
          }
          clearFindReplaceFocus();
          setFindReplaceOpen(false);
        }}
        columns={visibleProperties.map(p => p.label)}
        visibleRowIndices={findReplaceVisibleRowIndices}
        selectedRowIndices={selectedRows}
        onFind={handleFind}
        onReplace={handleReplace}
        onReplaceAll={handleReplaceAll}
        onHighlightMatch={handleFindReplaceHighlight}
      />

      {/* CSV Import Dialog */}
      <CSVImportPreviewDialog
        open={csvImportOpen}
        onClose={() => setCSVImportOpen(false)}
        onPreview={handleCSVPreview}
        onImport={handleCSVImport}
      />

      <QuickFillSaveReviewDialog
        open={effectiveSaveReviewOpen}
        previewOnly={saveReviewPreviewOnly}
        contentRef={saveReviewDialogRef}
        warning={effectiveSaveReviewWarning}
        result={effectiveSaveReviewResult}
        successRows={successRows}
        failedRows={failedRows}
        onClose={() => setSaveResultDialog({ open: false, result: null })}
        onContinue={() => {
          setSaveResultDialog({ open: false, result: null });
          onContinue();
        }}
      />

      <QuickFillNavigationGuardDialog
        open={pendingNavigation !== null}
        isContinuing={isContinuing}
        onStay={() => setPendingNavigation(null)}
        onLeave={() => {
          const nav = pendingNavigation;
          setPendingNavigation(null);
          nav?.();
        }}
      />

      <QuickFillApplyProgressDialog
        open={showQuickFillApplyProgress}
        progress={syncProgress}
      />

      <QuickFillGuideDialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        onStartTour={handleOpenTour}
        content={quickFillGuideContent}
      />
    </>
  );
};

export default QuickFillDialogShell;
